import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { PRODUCTS, telLink, waLink, track } from "../data/products";
import { useLang } from "../i18n";
import { FadeIn, MaskLine, Micro } from "../components/Reveal";
import PageTransition from "../components/PageTransition";
import ContactSection from "../components/ContactSection";
import Footer from "../components/Footer";

const SUPPLIERS = {
  "acp-sheets": [
    ["CityBOND", "https://citybond.in/exclusive-series"],
    ["VivaBOND", "https://vivaacp.com/products/aluminium-composite-panel/"],
    ["IMPERIA DECOR", "https://www.imperiadecor.com/"]
  ],
  "hpl-sheets": [
    ["merino", "https://www.merinolaminates.com/en/product-category/laminates/"],
    ["RENUBOND", "https://reynobondindia.com/hpl-sheet/"]
  ],
  glasses: [
    ["SAINT-GOBAIN", "https://in.saint-gobain-glass.com/products"],
    ["Gold Plus", "https://goldplusgroup.com/"]
  ]
};

const IMAGE_SETS = {
  "acp-sheets": [
    "https://image.made-in-china.com/2f0j00SPjqrotJEnug/Shandong-Acm-ACP-Aluminum-Composite-Panels-Sheet-Facade-Cladding-Sign-Boards-Materials.webp",
    "https://irrorwxhrokklq5p.ldycdn.com/cloud/ljBpjKillqSRmjnoqrkijq/acp-panel-800-800.jpg",
    "https://citybond.in/assets/images/products/others/3mm.webp"
  ],
  "hpl-sheets": [
    "https://image.made-in-china.com/318f0j00CahfZEqRglkm/HPL-mp4.webp",
    "https://image.made-in-china.com/202f0j00ZmsalWqhMPbO/HPL-High-Pressure-Laminate-Decorative-Sheet.webp",
    "https://image.made-in-china.com/202f0j00jAKQhokIMLby/HPL-High-Pressure-Laminate-Decorative-Sheet.webp"
  ],
  "acrylic-sheets": [
    "https://wholesalepos.com/images/stories/virtuemart/product/resized/Coloured-tinted-cast-acrylic-sheet_500x375.png",
    "https://wholesalepos.com/images/stories/virtuemart/product/resized/coloured-tinted-cast-acrylic-sheet-amber-300_500x375.jpg",
    "https://wholesalepos.com/images/stories/virtuemart/product/resized/coloured-tinted-cast-acrylic-sheet-blue-7703_500x375.jpg"
  ],
  glasses: [
    "https://sc04.alicdn.com/kf/Hf132fb568ec6441094b8a5f47d5e2d40w.jpg",
    "https://sc04.alicdn.com/kf/H9fc721dd45f14ab898600b57a9a104e4a.jpg",
    "https://sc04.alicdn.com/kf/H0555791c47244f64b756e6ca0808233b8.png"
  ],
  "pvc-soffit-panels": [
    "https://image.made-in-china.com/202f0j00aGvcKFOPASru/0-9mm-Thickness-Soffit-Perforated-Design-Extrusion-Plastic-PVC-Vinyl-Siding-PVC-External-Ceiling-Panels.webp",
    "https://image.made-in-china.com/202f0j00RfCcKQNaJjpg/0-9mm-Thickness-Soffit-Perforated-Design-Extrusion-Plastic-PVC-Vinyl-Siding-PVC-External-Ceiling-Panels.webp",
    "https://image.made-in-china.com/202f0j00GRCcjrOsnSgp/0-9mm-Thickness-Soffit-Perforated-Design-Extrusion-Plastic-PVC-Vinyl-Siding-PVC-External-Ceiling-Panels.webp"
  ],
  "wpc-fluted-panels": [
    "https://image.made-in-china.com/229f0j00FTmGAgodfHpI/WeChat-20230213123508-mp4.webp",
    "https://image.made-in-china.com/203f0j00VrQboafLjlgR/Classic-Interior-Design-Decorative-Vertical-WPC-Fluted-Wall-Panels.webp",
    "https://image.made-in-china.com/203f0j00VrTckuYMuWgf/Classic-Interior-Design-Decorative-Vertical-WPC-Fluted-Wall-Panels.webp"
  ],
  "3d-wall-panels": [
    "https://i.ebayimg.com/images/g/mpcAAOSwUvpj7Ihn/s-l1200.jpg",
    "https://image.made-in-china.com/2f0j00sSPtpIBlhhRK/Mywow-OEM-Packing-PVC-Decorative-Panels-3D-Wall-Panel.jpg",
    "https://image.made-in-china.com/202f0j00QmNaJtfhCOzc/Acoustic-Insulation-PVC-Wall-Panel-for-Construction-Material.webp"
  ],
  // Product 7-12 image sets refreshed with distinct product-focused photos.
  "uv-marble-sheets": [
    "https://image.made-in-china.com/2f0j00FQUcosTGsYrK/PVC-White-Marble-Sheet-Used-for-Interior-Decoration-UV-Wall-Panel.jpg",
    "https://image.made-in-china.com/202f0j00TYJowAvdftgf/UV-Imitation-Marble-Sheet-for-Interior-Decoration-Board.webp",
    "https://image.made-in-china.com/2f0j00UVWcQqhdnSgj/PVC-Placa-2800-X-1220-Madera-Interior-PVC-Panel-Decoration-PVC-Marble-Sheet-3D-WPC-Spc-UV-Board-PVC-Marble-Wall-Panel-Facade-Panels-for-TV-Background.webp"
  ],
  "false-ceiling": [
    "https://image.made-in-china.com/202f0j00yvuhOAfnylpC/Gypsum-Plasterboard-for-Ceiling-and-Partition-Size-1200X2400mm-Factory-Price-Supply-2400-1200-9mm-Gypsum-Board.webp",
    "https://images.homedepot-static.com/productImages/35e3242c-c75e-40f7-8383-df48bca70a35/svn/white-armstrong-ceilings-drop-ceiling-tiles-1205-64_1000.jpg",
    "https://s.alicdn.com/@sc04/kf/Hd7af3b744b0446e3b1740075516aeb36v/Interior-Design-Stars-Design-Mineral-Fiber-False-Ceiling-Good-Sound-Mineral-Fiber-Ceiling-Tiles.jpg"
  ],
  "aluminium-profiles": [
    "https://www.aluminium.lk/images/product-categories/MainCat/Other%20Profiles.png",
    "https://img.directindustry.com/images_di/photo-g/13749-20620950.jpg",
    "https://image.made-in-china.com/202f0j00YCgctwLrJHoI/New-Extruded-Profile-Powder-Coated-6063-Aluminum-Window-and-Door-Frame.webp"
  ],
  "cement-fiber-boards": [
    "https://www.secolite.eu/user/themes/secolite/assets/images/cement-board/img-2.jpg",
    "https://image.made-in-china.com/2f0j00TRpcHrwAHZba/Fiber-Cement-Board-Building-Materials-Supplies.webp",
    "https://www.51jcwpt.com/file/upload/202408/26/150545941.jpg.middle.jpg"
  ],
  "ms-pipes-angles": [
    "https://d91ztqmtx7u1k.cloudfront.net/ClientContent/Images/ExtraLarge/thickness-5mm-mild-steel-iron-20240622191514929.jpeg",
    "https://d91ztqmtx7u1k.cloudfront.net/ClientContent/Images/small/thickness-5mm-mild-steel-iron-20240622191525319.jpg",
    "https://d91ztqmtx7u1k.cloudfront.net/ClientContent/Images/small/thickness-5mm-mild-steel-iron-20240622191525180.jpeg"
  ]
};

const FALLBACK_PALETTE = {
  "ACP SHEETS": ["#77736d", "#c9a55b"], "HPL SHEETS": ["#3d3329", "#b78d61"],
  "ACRYLIC SHEETS": ["#365a68", "#9fd7e7"], "GLASSES": ["#263b43", "#b9d6df"],
  "PVC-SOFFIT PANELS": ["#d9d5ca", "#f3f0e8"], "WPC-FLUTED PANELS": ["#684c32", "#b58b60"],
  "3D WALL PANELS": ["#49443e", "#bdb4a7"], "UV MARBLE SHEETS": ["#d9d4cb", "#8f8a82"],
  "FALSE CEILING": ["#bdbdb7", "#f0f0eb"], "ALUMINIUM PROFILES": ["#777b7d", "#d7d9d8"],
  "CEMENT FIBER BOARDS": ["#77736d", "#aaa59a"], "MS PIPES — ANGLES": ["#34383b", "#8d9295"]
};

function fallbackImage(name, variant = 0) {
  const [a, b] = FALLBACK_PALETTE[name] || ["#555", "#aaa"];
  const safe = name.replace(/&/g, "&amp;");
  const patterns = {
    "ACP SHEETS": `<rect x="120" y="48" width="360" height="235" rx="8" fill="${a}"/><rect x="140" y="68" width="320" height="195" fill="${b}"/><path d="M160 220L300 90L440 220" fill="none" stroke="${a}" stroke-width="22"/>`,
    "HPL SHEETS": `<rect x="105" y="40" width="390" height="250" rx="6" fill="${a}"/><path d="M115 65h370v200H115z" fill="${b}"/><path d="M150 80v170M190 80v170M230 80v170M270 80v170M310 80v170M350 80v170M390 80v170M430 80v170" stroke="${a}" stroke-width="8" opacity=".65"/>`,
    "ACRYLIC SHEETS": `<rect x="100" y="55" width="400" height="230" rx="8" fill="${b}" opacity=".9"/><rect x="125" y="80" width="350" height="180" rx="6" fill="${a}" opacity=".62"/><path d="M140 95L450 245" stroke="#fff" stroke-width="12" opacity=".45"/>`,
    "GLASSES": `<rect x="115" y="45" width="370" height="260" fill="${a}"/><rect x="135" y="65" width="330" height="220" fill="${b}" opacity=".55"/><path d="M150 260L450 85" stroke="#fff" stroke-width="10" opacity=".45"/>`,
    "PVC-SOFFIT PANELS": `${[110,145,180,215,250,285,320,355,390,425,460].map(x => `<line x1="${x}" y1="55" x2="${x}" y2="275" stroke="${b}" stroke-width="10"/>`).join("")}<rect x="90" y="55" width="410" height="220" fill="${a}"/>`,
    "WPC-FLUTED PANELS": `<rect x="95" y="45" width="400" height="250" fill="${a}"/>${[120,150,180,210,240,270,300,330,360,390,420,450].map(x => `<rect x="${x}" y="55" width="18" height="230" rx="7" fill="${b}"/>`).join("")}`,
    "3D WALL PANELS": `<rect x="90" y="45" width="410" height="250" fill="${a}"/>${[120,200,280,360,440].map(x => `<path d="M${x} 60l45 45-45 45 45 45-45 45" fill="none" stroke="${b}" stroke-width="24"/>`).join("")}`,
    "UV MARBLE SHEETS": `<rect x="88" y="42" width="424" height="266" rx="5" fill="#ddd9d2"/><rect x="102" y="56" width="396" height="238" fill="#f5f3ef"/><path d="M105 115C165 82 205 158 255 120S350 78 495 135M110 210C175 165 220 255 285 190S385 155 495 220M180 58C210 95 235 88 265 58M350 294C380 250 420 268 495 250" fill="none" stroke="#8d8b87" stroke-width="7" opacity=".72"/><path d="M145 80C205 130 218 102 275 76M300 160C360 120 405 150 470 105" fill="none" stroke="#c9a55b" stroke-width="4" opacity=".9"/><rect x="98" y="52" width="404" height="246" fill="url(#marbleGlow)" opacity=".22"/>`,
    "FALSE CEILING": `<rect x="85" y="55" width="420" height="230" fill="${a}"/>${[110,190,270,350,430].map(x => `<rect x="${x}" y="75" width="60" height="190" fill="${b}" stroke="${a}" stroke-width="8"/>`).join("")}`,
    "ALUMINIUM PROFILES": `${[110,170,230,290,350,410].map((x, k) => `<rect x="${x}" y="55" width="32" height="240" rx="5" fill="${k % 2 ? b : a}" transform="rotate(${k % 2 ? 4 : -4} ${x + 16} 175)"/>`).join("")}`,
    "CEMENT FIBER BOARDS": `<rect x="90" y="50" width="410" height="240" fill="${a}"/>${[120,190,260,330,400].map(x => `<rect x="${x}" y="70" width="58" height="200" fill="${b}" stroke="${a}" stroke-width="5"/>`).join("")}`,
    "MS PIPES — ANGLES": `${[120,190,260,330,400].map((x, k) => `<g transform="translate(${k * 2} ${k * 3}) rotate(${k - 2} ${x + 18} 170)"><path d="M${x} 70h72v24h-48v181h-24z" fill="${k % 2 ? b : a}" stroke="#d4d5d4" stroke-width="3"/><path d="M${x + 10} 82h45" stroke="#f4f4f4" stroke-width="4" opacity=".45"/></g>`).join("")}`
  };
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 350"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#111"/><stop offset="1" stop-color="#2a2925"/></linearGradient><linearGradient id="marbleGlow" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#ffffff"/><stop offset="0.5" stop-color="#ffffff"/><stop offset="1" stop-color="#aaa"/></linearGradient></defs><rect width="600" height="350" fill="url(#g)"/>${patterns[name] || ""}<circle cx="${variant ? 500 : 95}" cy="${variant ? 55 : 305}" r="${variant ? 20 : 15}" fill="#c9a55b"/><text x="300" y="326" text-anchor="middle" fill="#eee9df" font-family="Arial" font-size="18" letter-spacing="3">${safe}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function ProductCard({ product, index }) {
  const [slide, setSlide] = useState(0);
  const [failed, setFailed] = useState({});
  const images = useMemo(() => IMAGE_SETS[product.slug] || [product.img, product.img, product.img], [product]);
  const suppliers = SUPPLIERS[product.slug] || [];
  const current = images[slide] || fallbackImage(product.name, slide);

  useEffect(() => {
    setSlide(0);
    setFailed({});
  }, [product.slug]);

  useEffect(() => {
    const timer = window.setInterval(() => setSlide((s) => (s + 1) % images.length), 3600);
    return () => window.clearInterval(timer);
  }, [images.length]);

  const next = () => setSlide((s) => (s + 1) % images.length);
  const prev = () => setSlide((s) => (s - 1 + images.length) % images.length);

  return (
    <article className="group border border-white/10 bg-coal/70 p-3 md:p-4 overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] tracking-[0.25em] text-gold">{product.num}</span>
        <Micro>{product.cat}</Micro>
      </div>
      <h2 className="font-display uppercase text-2xl md:text-3xl leading-none group-hover:text-gold transition-colors duration-500">{product.name}</h2>
      <p className="mt-2 mb-3 text-xs leading-relaxed text-cream/55">{product.full} — {product.cat === "INTERIOR" ? "interior" : "architectural"} material solution.</p>

      <div className="relative overflow-hidden border border-white/10 aspect-[16/10] bg-coal" data-cursor="view">
        <img
          src={failed[slide] ? fallbackImage(product.name, slide) : current}
          alt={`${product.name} product photo`}
          className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition duration-700"
          loading="lazy"
          onError={() => setFailed((f) => ({ ...f, [slide]: true }))}
        />
        <button type="button" onClick={prev} aria-label={`Previous ${product.name} image`} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-gold transition-colors"><ArrowLeft className="w-4 h-4" /></button>
        <button type="button" onClick={next} aria-label={`Next ${product.name} image`} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-gold transition-colors"><ArrowRight className="w-4 h-4" /></button>
      </div>
      <div className="flex justify-center gap-1.5 py-2" aria-label={`${product.name} images`}>
        {images.map((_, i) => <button key={i} type="button" aria-label={`Show image ${i + 1}`} aria-current={i === slide} onClick={() => setSlide(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === slide ? "bg-gold scale-110" : "bg-white/25"}`} />)}
      </div>

      <div className="border-t border-white/10 pt-2 mt-1 min-h-[45px]">
        {suppliers.length ? (
          <>
            <div className="text-[8px] tracking-[0.2em] uppercase text-white/35 mb-1.5">Suppliers</div>
            <div className="flex flex-wrap gap-1.5">
              {suppliers.map(([name, url], i) => <a key={name} href={url} target="_blank" rel="noopener noreferrer" title={`Open ${name} ${product.name} product page`} className={`inline-flex items-center rounded px-2 py-1 bg-cream text-[9px] font-semibold ${i === 1 ? "text-blue-700" : i === 2 ? "text-amber-700" : "text-black"}`}>{name}</a>)}
            </div>
          </>
        ) : <span className="text-[8px] tracking-[0.2em] uppercase text-white/25">Product</span>}
      </div>
    </article>
  );
}

export default function Catalogue() {
  const { hash } = useLocation();
  const { t } = useLang();
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    document.title = "Material Catalogue — INT–EXT";
    track("catalogue:view");
    return () => { document.title = "INT–EXT | Interior • Exterior • Products — Coimbatore"; };
  }, []);

  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 800);
    return () => clearTimeout(timer);
  }, [hash]);

  const visibleProducts = PRODUCTS.filter((p) => filter === "ALL" || p.cat === filter);

  return (
    <PageTransition>
      <main>
        <header className="px-5 md:px-12 pt-32 md:pt-44 pb-14 md:pb-20 border-b border-white/10">
          <Micro className="text-gold/80">{t("catalogue.micro")}</Micro>
          <h1 className="font-display uppercase leading-[0.92] tracking-tight text-[clamp(2.3rem,7vw,6rem)] mt-6">
            <MaskLine>{t("catalogue.h1a")}</MaskLine>
            <MaskLine delay={0.1} innerClass="text-outline-gold">{t("catalogue.h1b")}</MaskLine>
          </h1>
          <FadeIn delay={0.3} className="mt-8 flex flex-wrap items-end justify-between gap-6">
            <p className="text-sm md:text-base text-cream/55 max-w-md">{t("catalogue.p")}</p>
            <a href={telLink} data-testid="catalogue-talk" data-cursor="link" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-gold hover:text-goldbright transition-colors border-b border-gold/40 pb-1">
              {t("catalogue.talk")} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </FadeIn>
        </header>

        <section data-testid="catalogue-list" className="px-5 md:px-12 py-8 md:py-12">
          <div className="flex gap-7 overflow-x-auto pb-5 mb-2 scrollbar-hide">
            {["ALL", "INTERIOR", "EXTERIOR"].map((item) => <button key={item} type="button" onClick={() => setFilter(item)} className={`shrink-0 text-[10px] tracking-[0.2em] uppercase pb-2 border-b-2 transition-colors ${filter === item ? "text-gold border-gold" : "text-cream/45 border-transparent"}`}>{item}</button>)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
            {visibleProducts.map((p, i) => <FadeIn key={p.slug} delay={Math.min(i * 0.03, 0.15)}><div id={p.slug} className="scroll-mt-28"><ProductCard product={p} index={i} /></div></FadeIn>)}
          </div>
        </section>

        <ContactSection />
        <Footer />
      </main>
    </PageTransition>
  );
}
